import { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSpinner, faFilter } from '@fortawesome/free-solid-svg-icons';
import CategoryDropdown from '../../search/CategoryDropdown';
import SuggestionList from '../../search/SuggestionList';
import { getSearchCategories, getSearchSuggestions } from '../../../utils/searchApi';

const DEBOUNCE_MS = 300;

export default function Search_products({ onClick, onOpenProduct, filters, onFilterChange, onCategorySelect }) {
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('all');
  const [keyword, setKeyword] = useState('');
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    getSearchCategories()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const runSearch = useCallback((cat, kw) => {
    if (!kw || kw.trim().length === 0) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setLoading(true);

    getSearchSuggestions(cat, kw)
      .then((data) => {
        if (requestId !== requestIdRef.current) return;
        setSuggestions(data);
        setLoading(false);
        setHighlightedIndex(-1);
      })
      .catch(() => {
        if (requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(categoryId, keyword);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [keyword, categoryId, runSearch]);

  const handleCategoryChange = (id) => {
    setCategoryId(id);
    if (keyword.trim()) runSearch(id, keyword);

    const catObj = categories.find((c) => String(c.id) === String(id));
    onCategorySelect?.(id === 'all' ? null : catObj ? { id: catObj.id, name: catObj.name } : null);
  };

  // Manual typing invalidates any previously selected suggestion — fall
  // back to plain fuzzy search on the next submit.
  const handleInputChange = (e) => {
    setKeyword(e.target.value);
    setSelectedProductId(null);
    setOpen(true);
  };

  // Selecting a suggestion fills the search bar AND remembers the exact
  // product id, so search can filter by id  instead of comparing name strings.
  const handleSelect = (item) => {
    const name = item?.name || item?.description || item?.product_name || item?.title || '';
    if (!name) return;
    setKeyword(name);
    setSelectedProductId(item?.id ?? null);
    setOpen(false);
  };

  // Clicking search . If a suggestion is still selected (text
  // hasn't been edited since), filter by that exact product id. Otherwise
  // fall back to fuzzy name search.
  const submitSearch = () => {
    const kw = (keyword ?? '').trim();
    if (!kw) return;
    setOpen(false);

    if (selectedProductId != null) {
      onFilterChange?.('productId', String(selectedProductId));
    } else {
      onFilterChange?.('productId', '');
    }
    onFilterChange?.('name', kw);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitSearch();
      return;
    }

    if (!open || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showPopup = open && keyword.trim().length > 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
      <div style={{ display: 'flex', width: '100%', alignItems: 'stretch', boxSizing: 'border-box' }}>
        <CategoryDropdown categories={categories} value={categoryId} onChange={handleCategoryChange} />

        <div style={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'stretch', boxSizing: 'border-box' }}>
          <input
            type="text"
            value={keyword ?? ''}
            placeholder="Search products…"
            onChange={handleInputChange}
            onFocus={() => { if (keyword.trim()) setOpen(true); }}
            onKeyDown={handleKeyDown}
            style={{
              boxSizing: 'border-box',
              width: '100%',
              height: '44px',
              padding: '0 44px 0 14px',
              border: '1px solid #E2E8F0',
              borderRadius: '0',
              fontSize: '14px',
              color: '#0F172A',
              background: '#FFFFFF',
            }}
          />
          {loading && (
            <FontAwesomeIcon
              icon={faSpinner}
              spin
              style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', fontSize: '14px', pointerEvents: 'none' }}
            />
          )}
        </div>

        <button
          type="button"
          onClick={submitSearch}
          aria-label="Search"
          style={{
            boxSizing: 'border-box',
            width: '48px',
            height: '44px',
            flexShrink: 0,
            border: '1px solid #2563EB',
            borderLeft: 'none',
            borderRadius: '0 10px 10px 0',
            background: '#0dcaf0',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#1D4ED8'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#2563EB'; }}
        >
          <FontAwesomeIcon icon={faSearch} style={{ fontSize: '14px' }} />
        </button>
      </div>

      {onClick && (
        <button
          type="button"
          onClick={onClick}
          className="d-lg-none"
          style={{
            boxSizing: 'border-box',
            height: '44px',
            padding: '0 16px',
            border: '1px solid #E2E8F0',
            borderRadius: '10px',
            background: '#FFFFFF',
            color: '#334155',
            fontSize: '14px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0,
          }}
        >
          <FontAwesomeIcon icon={faFilter} />
          Filter
        </button>
      )}

      {showPopup && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          left: 0,
          right: onClick ? '124px' : 0,
          background: '#FFFFFF',
          border: '1px solid #E2E8F0',
          borderRadius: '14px',
          boxShadow: '0 20px 50px rgba(15,23,42,0.14)',
          zIndex: 70,
          overflow: 'hidden',
        }}>
          <SuggestionList
            suggestions={suggestions}
            loading={loading}
            keyword={keyword}
            highlightedIndex={highlightedIndex}
            onHover={setHighlightedIndex}
            onSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
}